import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TESTID } from "@/lib/testIds";
import { Mail, KeyRound, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { requestPasswordReset } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await requestPasswordReset(email);
      setInfo("If an account exists for that email, a reset link has been sent.");
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-armada-base flex items-center justify-center px-6">
      <div className="absolute inset-0 dashboard-grid opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-[440px] glass-strong rounded-2xl p-10 animate-fade-up">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#D4AF37] mb-6">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">PASSWORD RECOVERY</div>
        <h1 className="text-2xl font-black text-white mb-1">Reset Credentials</h1>
        <p className="text-sm text-zinc-400 mb-6 font-light">
          Enter your Commander email. We'll dispatch a sovereign reset signal.
        </p>

        {info && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-emerald-950/40 border border-emerald-900/50 text-emerald-200 text-sm">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /><span>{info}</span>
          </div>
        )}
        {error && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-red-950/40 border border-red-900/50 text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <input
            data-testid={TESTID.forgotEmail}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="commander@eigooinc.net"
            className="armada-input"
          />
          <button
            data-testid={TESTID.forgotSubmit}
            type="submit"
            disabled={loading || !email}
            className="btn-gold w-full flex items-center justify-center gap-2"
          >
            <Mail className="w-4 h-4" />
            {loading ? "Sending…" : "Send Reset Link"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { resetPassword } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setError("Passwords do not match"); return; }
    setError(""); setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-armada-base flex items-center justify-center px-6">
      <div className="absolute inset-0 dashboard-grid opacity-30 pointer-events-none" />
      <div className="relative w-full max-w-[440px] glass-strong rounded-2xl p-10 animate-fade-up">
        <div className="text-[10px] tracking-armada text-[#D4AF37] font-bold mb-2">NEW CREDENTIALS</div>
        <h1 className="text-2xl font-black text-white mb-1">Set New Password</h1>
        <p className="text-sm text-zinc-400 mb-6 font-light">Choose a strong password (min 8 characters).</p>

        {done && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-emerald-950/40 border border-emerald-900/50 text-emerald-200 text-sm">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Password reset. Redirecting to login…</span>
          </div>
        )}
        {error && (
          <div className="mb-4 flex items-start gap-2 p-3 rounded-md bg-red-950/40 border border-red-900/50 text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <input
            data-testid={TESTID.resetPassword}
            type="password"
            required
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password"
            className="armada-input"
          />
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="armada-input"
          />
          <button
            data-testid={TESTID.resetSubmit}
            type="submit"
            disabled={loading || done || !token}
            className="btn-gold w-full flex items-center justify-center gap-2"
          >
            <KeyRound className="w-4 h-4" />
            {loading ? "Resetting…" : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
}
